// AI の遊び点検 (覚醒王 10-09・docs/research/2026-10-09-awoken-fresh-eyes.md) の本物 R4・R2。
//   2026-10-08 ユーザー採用 (検収 F-2「こちらがいい」) = R4 の big が配布版の既定。R2 (deathFront) は「前のまま」(F-3) で不採用 = 既定 off・DEV 比較用に残す。
//   DEV の切り替えは src/main.ts の DEV の箱で URL を読む (scripts/check_dev_leak.mjs に defeatHint・deathFront・__freshEyesFix を登録)。R1 (覚醒王の体の壁) は balance.ts。
//
// R4 負け画面の「再演」の下の一言 (render.ts deathTipFor) を読める大きさ・濃さにする (既定 = big)。文言の選び方は変えない (F-2b は A＝今のまま)。DEV ?defeatHint=now で旧へ。
//   旧 (now) = 15px の明朝・色 (176,158,204) α0.9 × 濃さ 0.75・暗幕 0.86 の上 (2倍に拡大してやっと読める)。
// R2 ?deathFront=1: 体力0から暗転・負け画面までの間だけ、伏した主人公を世界の層の一番手前 (噛みの光などの FX より後) に描き、
//   体の縁へ薄い月光の縁取りを付ける。ほかの時間の描く順・縁取りは変えない。
//   ⚠️ 実機で確かめた事実: 主人公はもともとボスより後 (= 手前) に描いている (render.ts draw の drawBoss → drawPlayer)。
//   「獣の下に入って見えない」は描く順ではなく、暗い色同士が重なるのと、噛みの光 (drawFx) が主人公の上に乗るのが原因。
//   なのでこの比較版は「FX より手前へ」＋「縁の光」で見分けやすくする。

export type DefeatHintMode = 'now' | 'big'
export type DeathFrontMode = 'off' | 'on'

let defeatHint: DefeatHintMode = 'big'
let deathFront: DeathFrontMode = 'off'

export function parseDefeatHintMode(v: string | null | undefined): DefeatHintMode | null {
  return v === 'big' || v === 'now' ? v : null
}
export function parseDeathFrontMode(v: string | null | undefined): DeathFrontMode | null {
  if (v === '1' || v === 'on') return 'on'
  if (v === '0' || v === 'off') return 'off'
  return null
}

export function setDefeatHintMode(m: DefeatHintMode): void { if (import.meta.env.DEV) defeatHint = m }
export function defeatHintMode(): DefeatHintMode { return import.meta.env.DEV ? defeatHint : 'big' }
export function setDeathFrontMode(m: DeathFrontMode): void { if (import.meta.env.DEV) deathFront = m }
export function deathFrontMode(): DeathFrontMode { return import.meta.env.DEV ? deathFront : 'off' }

export type DefeatHintStyle = {
  font: string
  color: string
  alphaMul: number      // フェードインの到達値 (旧 0.75・big 0.92)
  shadow: { color: string; blur: number } | null
  hintGapPx: number     // 下の難易度ヒントまでの行送り (旧 26・big 30)
}

const MINCHO = '"Yu Mincho","Hiragino Mincho ProN",serif'

export function defeatHintStyle(m: DefeatHintMode = defeatHintMode()): DefeatHintStyle {
  if (m === 'big') {
    // 21px (旧の 1.4 倍)・明るい薄紫・濃さ 0.92・暗い影で暗幕から浮かせる。一番長い文 (36 字) でも 760px 弱 = 画面 960 に収まる。
    //   明るさは「再演」の字 (#cdb8ec) と同じくらいまで (それより明るいと、押す場所より一言の方が目立つ)。
    return {
      font: `500 21px ${MINCHO}`,
      color: 'rgba(206,194,232,1)',
      alphaMul: 0.92,
      shadow: { color: 'rgba(0,0,0,0.9)', blur: 4 },
      hintGapPx: 30,
    }
  }
  return { font: `400 15px ${MINCHO}`, color: 'rgba(176,158,204,0.9)', alphaMul: 0.75, shadow: null, hintGapPx: 26 }
}

/** 死の一拍〜負け画面の間か (この間だけ R2 の比較版が効く)。 */
export function inDeathWindow(state: { deathBeat: unknown; phase: string }): boolean {
  return !!state.deathBeat || state.phase === 'defeat'
}

/** R2 の比較版がいま効くか。 */
export function deathFrontActive(state: { deathBeat: unknown; phase: string }): boolean {
  return deathFrontMode() === 'on' && inDeathWindow(state)
}

/** 縁の光の濃さ (0..1)。死の一拍の頭 0.25 秒で立ち上げる (いきなり光らせない)。負け画面では満ちたまま (暗幕が上から沈める)。 */
export function deathRimStrength(state: { deathBeat: { elapsedMs: number } | null; phase: string }): number {
  if (state.phase === 'defeat') return 1
  if (!state.deathBeat) return 0
  return Math.max(0, Math.min(1, state.deathBeat.elapsedMs / 250))
}
